// The cue watcher (R547): the effects that no message and no event carry.
//
// Most sounds have a sender. A toast names an event id and the bus plays the
// row's cue; a blast carries a place and `triggers.ts` names the voice. What
// is left is a set of facts the wire states but never announces: a reload
// that finished, a hull that a hostile now hears, a deposit that ran dry, a
// captain who reached the top of a career. The server sends the numbers. It
// sends no notice, because a notice for each of these would bury the ones
// that matter.
//
// So this module watches the frames. Every rule below is one EDGE between
// the frame that was and the frame that came, and each edge is a small pure
// function with its own test. `CueWatch` holds the previous picture, calls
// them, and turns an edge into a cue. It decides nothing else.
//
// **A delta frame carries the CHANGED fleets alone** (protocol v4). A hull
// that is not in the frame did not change, so the previous picture keeps its
// figures and only `State.removed` takes an entry out. A frame with `full`
// set replaces everything: it primes the picture and cues nothing, because a
// hello, a season roll and a dropped frame are not news about the water.
//
// **A player-direct cue goes through the event bus**, not through the
// director: the thirteen ids below have a row in `data/product/events.toml`,
// so an operator silences one or gives it a toast without a code change.
// The thirteen map cues go to the map's `cue`, which damps by distance
// and by zoom. The six figures the rules read are knobs of
// `data/presentation/presentation.toml`; not one of them is a literal here.
// The six hull edges of R3122 are map cues at the hull: the shield that
// falls, the magazine that runs out, the round selection, the signature
// order that is ready again, the contact that breaks and the hostile
// Beacon that sets its anchor.
// Three client-local events of R3135 have no place, so they go through the
// bus: a quick line or a map marker of a squad mate, a step of the sea
// trials, and a new blueprint with a module of a top grade.
// One more line, the readiness a fatigue cue speaks at, is not a knob: it
// belongs to the hull, so the wire states it per fleet (R769).
//
// **Two rules read a clock and not a frame** (R550). The world clock and
// the founding truce move on their own, so the day cue and the truce tick
// are edges between two RENDER frames and not between two wire frames.
//
// See `docs/design/audio.md` §4.6 and §8.6.

import {
  AnchorState, Mission, ShipClass, SignatureCharge, SiteKind, TorpedoKind,
} from '../gen/proto/aquaphobia';
import type {
  Captain, Empire, ModuleGrade, State, TechSpec,
} from '../gen/proto/aquaphobia';
import { DAWN_HOUR, DUSK_HOUR } from '../gen/balance';
import {
  AUDIO_BEACON_PULSE_SEC, AUDIO_MASTERWORK_TOP_GRADES, AUDIO_ROUND_SELECT_WAIT_SEC,
  AUDIO_TORPEDO_INCOMING_PX, AUDIO_TRUCE_TICK_SEC,
} from '../gen/presentation';
import { cellAt, mpxToPx } from '../world';
import { SEC_PER_HOUR, secOfDay } from '../locale/clock';

// -- the event ids -----------------------------------------------------------

/**
 * The events the client raises for itself. Each one has a cue-only row in
 * the event table, so the bus plays it and toasts nothing.
 *
 * They are constants and not literals because more files raise them: this
 * module, `fast/commands.ts`, which sends the order that plots a course, and
 * `fast/feed.ts`, which hears a quick line.
 */
export const EV_BUILD_QUEUED = 'build_queued';
export const EV_COURSE_PLOTTED = 'course_plotted';
export const EV_RELOAD_COMPLETE = 'reload_complete';
export const EV_FLEET_FATIGUED = 'fleet_fatigued';
export const EV_WE_ARE_DETECTED = 'we_are_detected';
export const EV_TECH_UNLOCKED = 'tech_unlocked';
export const EV_DEPOSIT_DEPLETED = 'deposit_depleted';
export const EV_BLUEPRINT_ACQUIRED = 'blueprint_acquired';
export const EV_EXPEDITION_DEPARTED = 'expedition_departed';
export const EV_CAPTAIN_PROMOTED = 'captain_promoted';
export const EV_DAY_NIGHT = 'day_night';
export const EV_TRUCE_TICK = 'truce_tick';
export const EV_SQUAD_PING = 'squad_ping';
export const EV_TRIAL_PASSED = 'trial_passed';
export const EV_BLUEPRINT_MASTERWORK = 'blueprint_masterwork';

// -- the shapes the rules read -----------------------------------------------
//
// Each rule asks for the fields it uses and nothing more, so a test drives it
// with a plain object and a caller hands it a wire `FleetState` unchanged.

/** A point in world pixels. */
export interface Pt { x: number; y: number }

/** One cue with a place: the id and where it sounds. */
export interface PlacedCue extends Pt { id: number }

interface HasId { id: number }
interface Reloading extends HasId { reloadSec: number }
interface Tiring extends HasId { readinessPct: number; readinessFloorPct: number }
interface Hearable extends HasId { heard: boolean }
interface Scanning extends HasId { scanReadySec: number; xMpx: number; yMpx: number }
interface Anchoring extends HasId { anchor: AnchorState; cls: ShipClass; xMpx: number; yMpx: number }
interface Placed extends HasId { xMpx: number; yMpx: number }
interface Flying extends Placed { faction: number; headingMrad: number }
interface Needs extends HasId { requires: number[] }
interface Shielded extends Placed { shieldPct: number }
interface Loaded extends Placed { rounds: readonly { count: number }[]; reloadSec: number }
interface Selecting extends Placed { activeRound: TorpedoKind }
interface Charged extends Placed { signatureCharge: SignatureCharge }
interface Quieting extends Placed { quiet: boolean }
interface Contact extends Placed { faction: number; cls: ShipClass; anchor: AnchorState }

// -- the edges ---------------------------------------------------------------

/** The ids of `next` that `prev` did not hold. */
export function diffIds(prev: ReadonlySet<number>, next: Iterable<number>): number[] {
  const out: number[] = [];
  for (const id of next) if (!prev.has(id)) out.push(id);
  return out;
}

/**
 * Fleets whose reload finished: the countdown was above zero and is zero.
 *
 * A hull the caller never saw before has no previous figure, so its first
 * frame is never an edge.
 */
export function reloadEdges(
  prev: ReadonlyMap<number, number>, fleets: readonly Reloading[],
): number[] {
  const out: number[] = [];
  for (const f of fleets) {
    const was = prev.get(f.id);
    if (was !== undefined && was > 0 && f.reloadSec === 0) out.push(f.id);
  }
  return out;
}

/**
 * Fleets whose crew reached the bottom: readiness was above the hull's own
 * floor and is now at it or under it (R769).
 *
 * **The line is the hull's, and never one figure for the map.** The wire
 * states `readiness_floor_pct` per fleet, because the Command attribute of
 * the captain aboard lifts the floor: an uncaptained hull speaks at 50%, a
 * hull under a peaked captain speaks at 72%. A crew that is merely tired
 * says nothing at all.
 *
 * **Zero is "not stated"**, never an edge. The wire drops a zero field, so
 * a frame that says nothing about readiness and a frame that reports a
 * spent crew look the same. The cue must not fire on the first. A frame
 * with no floor states no line, so it raises no cue either.
 */
export function fatigueEdges(
  prev: ReadonlyMap<number, number>, fleets: readonly Tiring[],
): number[] {
  const out: number[] = [];
  for (const f of fleets) {
    const was = prev.get(f.id);
    if (was === undefined || was === 0 || f.readinessPct === 0) continue;
    if (f.readinessFloorPct === 0) continue;
    if (was > f.readinessFloorPct && f.readinessPct <= f.readinessFloorPct) out.push(f.id);
  }
  return out;
}

/**
 * Fleets a hostile faction started to hear. `prev` holds the ids that were
 * heard already, so a hull that slips back into the quiet and is heard again
 * cues twice.
 */
export function heardEdges(
  prev: ReadonlySet<number>, fleets: readonly Hearable[],
): number[] {
  const out: number[] = [];
  for (const f of fleets) if (f.heard && !prev.has(f.id)) out.push(f.id);
  return out;
}

/** Fleets whose scan started: the countdown was zero and is above zero. */
export function scanEdges(
  prev: ReadonlyMap<number, number>, fleets: readonly Scanning[],
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of fleets) {
    const was = prev.get(f.id);
    if (was === 0 && f.scanReadySec > 0) out.push(placed(f));
  }
  return out;
}

/**
 * The anchor cue of a hull class. Each anchor hull has its own cue (R1143):
 * the Sentinel and the Tender speak their own line. The Beacon, and every
 * class the wire may add later, keeps `sfx-beacon-anchor`.
 */
function anchorCue(cls: ShipClass): string {
  switch (cls) {
    case ShipClass.SHIP_CLASS_SENTINEL: return 'sfx-sentinel-anchor';
    case ShipClass.SHIP_CLASS_TENDER: return 'sfx-tender-anchor';
    default: return 'sfx-beacon-anchor';
  }
}

/**
 * Fleets whose cable reached the floor: the anchor state became SET. Each
 * edge carries the cue of its hull class (R1143).
 */
export function anchorEdges(
  prev: ReadonlyMap<number, AnchorState>, fleets: readonly Anchoring[],
): [string, PlacedCue][] {
  const out: [string, PlacedCue][] = [];
  for (const f of fleets) {
    if (f.anchor !== AnchorState.ANCHOR_STATE_SET) continue;
    if (prev.get(f.id) === AnchorState.ANCHOR_STATE_SET) continue;
    out.push([anchorCue(f.cls), placed(f)]);
  }
  return out;
}

/**
 * The tech nodes this empire may now research and could not before: every
 * requirement is complete now, and at least one was open before.
 *
 * A node the empire already completed is not an unlock.
 */
export function unlockedTech(
  nodes: readonly Needs[], prevTech: ReadonlySet<number>, nextTech: ReadonlySet<number>,
): number[] {
  const out: number[] = [];
  for (const n of nodes) {
    if (nextTech.has(n.id)) continue;
    if (!n.requires.every((r) => nextTech.has(r))) continue;
    if (n.requires.every((r) => prevTech.has(r))) continue;
    out.push(n.id);
  }
  return out;
}

/**
 * The hostile rounds that are new this frame and are running at one of this
 * commander's hulls.
 *
 * A round holds its heading, so the test is a ray and not a distance: the
 * hull must be AHEAD of the round, and the round's line must pass within
 * `withinPx` of it. A round that is level with a hull or past it is not an
 * approach, and a round that runs at another empire is not this
 * commander's warning. The caller cues each round id once.
 *
 * `ownFleets` is a list or a map of the places of the own hulls: the rule
 * walks its `values()` once for each new hostile round, so the watcher
 * hands its own map and copies no list a frame.
 */
export function incomingTorpedoes(
  prevIds: ReadonlySet<number>, torpedoes: readonly Flying[],
  ownFleets: { values(): Iterable<Pt> }, ownFaction: number,
  withinPx: number = AUDIO_TORPEDO_INCOMING_PX,
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const t of torpedoes) {
    if (t.faction === ownFaction || prevIds.has(t.id)) continue;
    const tx = mpxToPx(t.xMpx), ty = mpxToPx(t.yMpx);
    const dx = Math.cos(t.headingMrad / 1000), dy = Math.sin(t.headingMrad / 1000);
    for (const h of ownFleets.values()) {
      const vx = h.x - tx, vy = h.y - ty;
      const along = vx * dx + vy * dy;
      if (along < 0) continue; // the hull is behind the round
      if (Math.abs(vx * dy - vy * dx) > withinPx) continue;
      out.push({ id: t.id, x: tx, y: ty });
      break;
    }
  }
  return out;
}

/**
 * Own hulls whose shield fell: the shield was above 0 and is 0 (R3122).
 * The wire states the shield of an own hull on every frame that carries
 * it, so 0 is a figure here and not a field the frame left out. A hull
 * with no shield is never above 0, so it never cues.
 */
export function shieldsDownEdges(
  prev: ReadonlyMap<number, number>, fleets: readonly Shielded[],
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of fleets) {
    const was = prev.get(f.id);
    if (was !== undefined && was > 0 && f.shieldPct === 0) out.push(placed(f));
  }
  return out;
}

/** The rounds aboard one hull, every kind together. */
export function roundsAboard(f: { rounds: readonly { count: number }[] }): number {
  let n = 0;
  for (const r of f.rounds) n += r.count;
  return n;
}

/**
 * Own hulls whose last round left the tube (R3122): the rounds aboard were
 * above 0, they are 0, and the tube reloads. The reload is the proof of a
 * shot: a magazine that empties in a harbour leaves no reload behind.
 * `prev` holds the rounds aboard of the frame before.
 */
export function magazineEmptyEdges(
  prev: ReadonlyMap<number, number>, fleets: readonly Loaded[],
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of fleets) {
    const was = prev.get(f.id);
    if (was !== undefined && was > 0 && roundsAboard(f) === 0 && f.reloadSec > 0) {
      out.push(placed(f));
    }
  }
  return out;
}

/**
 * Own hulls whose active round changed on this commander's order (R3121).
 * `ordered` holds the clock of each selection order the commander sent,
 * by hull. The sim also moves the active round when a kind runs out or a
 * load arrives, so a change with no order inside `waitSec` is silent.
 */
export function roundSelectEdges(
  prev: ReadonlyMap<number, TorpedoKind>, ordered: ReadonlyMap<number, number>,
  nowSec: number, fleets: readonly Selecting[],
  waitSec: number = AUDIO_ROUND_SELECT_WAIT_SEC,
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of fleets) {
    const was = prev.get(f.id);
    if (was === undefined || was === f.activeRound) continue;
    const at = ordered.get(f.id);
    if (at !== undefined && nowSec - at <= waitSec) out.push(placed(f));
  }
  return out;
}

/** Own hulls whose signature order is ready again: SPENT became READY
 *  (R3122). */
export function signatureReadyEdges(
  prev: ReadonlyMap<number, SignatureCharge>, fleets: readonly Charged[],
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of fleets) {
    if (prev.get(f.id) === SignatureCharge.SIGNATURE_CHARGE_SPENT
        && f.signatureCharge === SignatureCharge.SIGNATURE_CHARGE_READY) out.push(placed(f));
  }
  return out;
}

/**
 * Own hulls that broke contact (R3122): the `quiet` mark rose, and a
 * hostile heard the hull before it. `contacted` holds the hulls a hostile
 * heard since their last quiet. The mark is the all-clear the server
 * gives late (`FleetState.quiet`), so the cue does not sound on every
 * gap in the hostile's hearing.
 */
export function contactBrokenEdges(
  prevQuiet: ReadonlyMap<number, boolean>, contacted: ReadonlySet<number>,
  fleets: readonly Quieting[],
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of fleets) {
    if (f.quiet && prevQuiet.get(f.id) === false && contacted.has(f.id)) out.push(placed(f));
  }
  return out;
}

/**
 * Beacons of another faction that set their anchor in sight (R3122).
 * `prev` holds the anchor state of each foreign Beacon of the frame
 * before, so a Beacon that comes into sight with its anchor already set
 * is not an edge.
 */
export function hostileBeaconEdges(
  prev: ReadonlyMap<number, AnchorState>, contacts: readonly Contact[], ownFaction: number,
): PlacedCue[] {
  const out: PlacedCue[] = [];
  for (const f of contacts) {
    if (f.faction === ownFaction || f.cls !== ShipClass.SHIP_CLASS_BEACON) continue;
    if (f.anchor !== AnchorState.ANCHOR_STATE_SET) continue;
    const was = prev.get(f.id);
    if (was !== undefined && was !== AnchorState.ANCHOR_STATE_SET) out.push(placed(f));
  }
  return out;
}

/** The anchored Beacon closest to the view centre, or nothing. */
export function nearestSetBeacon(
  beacons: ReadonlyMap<number, Pt>, viewCentre: Pt,
): PlacedCue | null {
  let best: PlacedCue | null = null;
  let bestD = Infinity;
  for (const [id, p] of beacons) {
    const dx = p.x - viewCentre.x, dy = p.y - viewCentre.y;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = { id, x: p.x, y: p.y }; }
  }
  return best;
}

/** A worked deposit ran dry: the amount was above zero and is zero. */
export function depletedDeposit(
  prevAmount: number | undefined, amount: number, targeted: boolean,
): boolean {
  return targeted && prevAmount !== undefined && prevAmount > 0 && amount === 0;
}

/**
 * Whether the world clock reads day at `clockSec`.
 *
 * The day is 86400 game-seconds whatever the timescale is, and the reading
 * wraps at midnight (`state.ts`). Day runs from `dawnHour` to `duskHour`,
 * and every other hour is night. The caller sounds the cue on the change
 * alone, so one function states both crossings. The two hours are
 * `[world.day]` of the compile-time lane, because the hub pays the club
 * share of the casino on the same crossing (casino.md §16, R2144).
 */
export function isDay(
  clockSec: number, dawnHour: number = DAWN_HOUR,
  duskHour: number = DUSK_HOUR,
): boolean {
  const hour = secOfDay(clockSec) / SEC_PER_HOUR;
  return hour >= dawnHour && hour < duskHour;
}

/**
 * Whether one render frame of the founding truce owes a tick.
 *
 * The rule is the arcade clock tick's (`arcade/audio.ts`): the tick fires
 * on the frame that crosses each whole second inside the last `windowSec`
 * seconds. `prevLeft` is negative before the first frame, which fires
 * nothing — a client that joins with 8 seconds of truce left must not tick
 * 8 times at once. A truce that is over reads 0, and 0 owes no tick.
 */
export function truceTick(
  prevLeft: number, left: number, windowSec: number = AUDIO_TRUCE_TICK_SEC,
): boolean {
  if (prevLeft < 0) return false;
  return left > 0 && left <= windowSec
    && Math.ceil(left) !== Math.ceil(prevLeft);
}

// -- the cues of R3135 ---------------------------------------------------------

/**
 * Whether a step of the sea trials passed: one bit of the mask rose. The
 * card must show (`Empire.sea_trials_on`), so a veteran's seat says
 * nothing. `prev` is null before the first frame, which is never an edge.
 */
export function trialPassed(prev: number | null, mask: number, on: boolean): boolean {
  return on && prev !== null && ((mask & ~prev) >>> 0) !== 0;
}

/**
 * The grade codes a masterwork module carries: the top `top` grades of the
 * ladder the catalog states (`CatalogRules.module_grades`), which climbs.
 */
export function masterworkGrades(
  grades: readonly Pick<ModuleGrade, 'code'>[], top: number = AUDIO_MASTERWORK_TOP_GRADES,
): Set<string> {
  return new Set(grades.slice(Math.max(0, grades.length - top)).map((g) => g.code));
}

/** Whether one of the new prints holds a module of a masterwork grade. */
export function hasMasterwork(
  prints: readonly { id: number; modules?: readonly { grade: string }[] }[],
  newIds: readonly number[], top: ReadonlySet<string>,
): boolean {
  if (newIds.length === 0 || top.size === 0) return false;
  const fresh = new Set(newIds);
  return prints.some((b) => fresh.has(b.id) && (b.modules ?? []).some((m) => top.has(m.grade)));
}

/**
 * Whether a chat line is a signal of a squad mate: a live quick line (a
 * rally is one) from another commander, on the squad channel or from an
 * account of the squad. Every other line keeps the chat blip.
 */
export function squadPingLine(
  line: { history: boolean; quick: number; fromId: number },
  squadChannel: boolean, selfId: number, mates: ReadonlySet<number>,
): boolean {
  if (line.history || line.quick <= 0 || line.fromId === 0 || line.fromId === selfId) return false;
  return squadChannel || mates.has(line.fromId);
}

/** The ids of the markers a squad mate placed since the list before. */
export function mateMarkerEdges(
  prev: ReadonlySet<number>, list: readonly { id: number; ownerId: number }[],
  selfId: number, mates: ReadonlySet<number>,
): number[] {
  const out: number[] = [];
  for (const m of list) {
    if (!prev.has(m.id) && m.ownerId !== selfId && mates.has(m.ownerId)) out.push(m.id);
  }
  return out;
}

/** One fleet's place, in world pixels. */
function placed(f: Placed): PlacedCue {
  return { id: f.id, x: mpxToPx(f.xMpx), y: mpxToPx(f.yMpx) };
}

/** Writes one hull's place into `at`, in world pixels. A place the map
 *  holds already takes the new figures in place, so a state frame makes
 *  no object for a hull the watcher knows. */
function setPlace(at: Map<number, Pt>, f: Placed): void {
  const p = at.get(f.id);
  if (p) {
    p.x = mpxToPx(f.xMpx);
    p.y = mpxToPx(f.yMpx);
  } else {
    at.set(f.id, { x: mpxToPx(f.xMpx), y: mpxToPx(f.yMpx) });
  }
}

// -- the watcher -------------------------------------------------------------

/** What the watcher is handed. It reads these and holds none of the world. */
export interface CueDeps {
  /** Raises a client-local event. The bus plays its row's cue. */
  local(id: string): void;
  /** A cue with a place. The director damps it by distance and by zoom. */
  cue(id: string, x: number, y: number, scale?: number): void;
  /** The clock, in seconds. The damage gap and the pulse read it. */
  nowSec(): number;
  /** The world's time of day, in game-seconds. The day cue reads it. */
  worldClockSec(): number;
  /** Founding-truce game-seconds left, 0 once weapons are live. */
  truceLeftSec(): number;
  /**
   * The site table, by id. `onSiteOwner` names a site and no place, and the
   * depleted-deposit rule must know which cell that site sits on.
   */
  sites(): ReadonlyMap<number, { cell: number; kind: SiteKind }>;
}

/**
 * The previous picture, and the rules that read it.
 *
 * One cue per id per frame: each block below fires at most one cue, however
 * many hulls made the edge. Three reloads that finish together are one
 * sound, because three of the same sound in one frame is one loud sound.
 */
export class CueWatch {
  private primedState = false;
  private primedEmpire = false;

  // The state picture, own fleets unless the field says otherwise.
  private readonly ownIds = new Set<number>();
  private readonly reload = new Map<number, number>();
  private readonly readiness = new Map<number, number>();
  private readonly heard = new Set<number>();
  private readonly scan = new Map<number, number>();
  /**
   * Every OWN fleet's anchor state. A Beacon is public (R114), but another
   * empire's cable makes no sound here (R618): a busy map full of foreign
   * Beacons pulsed and clanked over everything the commander did.
   */
  private readonly anchor = new Map<number, AnchorState>();
  /** The commander's own anchored Beacons, by id, for the pulse (R618). */
  private readonly beacons = new Map<number, Pt>();
  private readonly torpedoIds = new Set<number>();
  /**
   * Where each own hull was last stated, in world pixels. A delta frame
   * carries the hulls that MOVED, so a hull that lies still is not in it —
   * and a round runs at a hull that lies still as readily as at one under
   * way. The approach rule therefore reads this map and not the frame.
   */
  private readonly ownAt = new Map<number, Pt>();
  /** The cell each own fleet works, for the fleets that gather. */
  private readonly gatherCell = new Map<number, number>();
  // The hull edges of R3122, own fleets.
  private readonly shield = new Map<number, number>();
  private readonly aboard = new Map<number, number>();
  private readonly active = new Map<number, TorpedoKind>();
  private readonly charge = new Map<number, SignatureCharge>();
  private readonly quiet = new Map<number, boolean>();
  /** The own hulls a hostile heard since their last quiet. */
  private readonly contacted = new Set<number>();
  /** The clock of each selection order this commander sent, by hull. */
  private readonly roundOrders = new Map<number, number>();
  /** The anchor state of every foreign Beacon in sight (R3122). */
  private readonly foreignAnchor = new Map<number, AnchorState>();

  // The empire picture.
  private readonly queue = new Set<number>();
  private readonly blueprints = new Set<number>();
  private readonly expeditions = new Set<number>();
  private readonly tech = new Set<number>();
  private readonly phase = new Map<number, string>();
  /** The sea trials mask of the last frame, or null before the first. */
  private trials: number | null = null;
  /** The map markers of the last list, or null before the first list of
   *  a hello (R3135). */
  private markers: Set<number> | null = null;

  /** The last amount each site reported, for the deposits that run dry. */
  private readonly siteAmount = new Map<number, number>();

  private lastPulseSec = -Infinity;
  /** What the last frame called the light, or null before the first one. */
  private wasDay: boolean | null = null;
  /** The truce the last frame saw. Negative before the first frame. */
  private prevTruce = -1;

  constructor(private readonly d: CueDeps) {}

  /**
   * This commander sent a round selection for one hull (R3121). The order
   * funnel calls it, so the change of the active round that answers the
   * order speaks and a change the sim makes alone does not.
   */
  noteRoundOrder(fleetId: number): void {
    this.roundOrders.set(fleetId, this.d.nowSec());
  }

  /**
   * The commander's own snapshot: the queue, the prints, the expeditions,
   * the tech, the captains and the sea trials.
   *
   * The first frame primes and cues nothing. Every later frame is a
   * comparison against the one before it. A new print with a module of a
   * top grade of `grades` plays the masterwork cue in place of the print
   * cue (R3135).
   */
  onEmpire(
    e: Empire, catalogTech: readonly TechSpec[], grades: readonly Pick<ModuleGrade, 'code'>[] = [],
  ): void {
    const queueIds = e.queue.map((j) => j.id);
    const printIds = e.blueprints.map((b) => b.id);
    const sailedIds = e.expeditions.map((x) => x.id);
    const queued = diffIds(this.queue, queueIds);
    const prints = diffIds(this.blueprints, printIds);
    const sailed = diffIds(this.expeditions, sailedIds);
    const nextTech = new Set(e.tech);
    const opened = unlockedTech(catalogTech, this.tech, nextTech);
    const promoted = this.promotions(e.captains);
    const mask = e.seaTrials ?? 0;
    const passed = trialPassed(this.trials, mask, e.seaTrialsOn === true);
    this.trials = mask;

    fill(this.queue, queueIds);
    fill(this.blueprints, printIds);
    fill(this.expeditions, sailedIds);
    fill(this.tech, nextTech);
    this.phase.clear();
    for (const c of e.captains) this.phase.set(c.id, c.phase);

    if (!this.primedEmpire) { this.primedEmpire = true; return; }
    if (queued.length) this.d.local(EV_BUILD_QUEUED);
    if (prints.length) {
      this.d.local(hasMasterwork(e.blueprints, prints, masterworkGrades(grades))
        ? EV_BLUEPRINT_MASTERWORK : EV_BLUEPRINT_ACQUIRED);
    }
    if (sailed.length) this.d.local(EV_EXPEDITION_DEPARTED);
    if (opened.length) this.d.local(EV_TECH_UNLOCKED);
    if (promoted) this.d.local(EV_CAPTAIN_PROMOTED);
    if (passed) this.d.local(EV_TRIAL_PASSED);
  }

  /**
   * A hello arrived. The next list of map markers is the state of this
   * world and not news, so it primes the marker rule (R3135).
   */
  onHello(): void {
    this.markers = null;
  }

  /**
   * The whole list of map markers. A marker that a squad mate placed since
   * the list before plays the squad signal; the first list of a hello
   * primes and plays nothing (R3135).
   */
  onMarkers(
    list: readonly { id: number; ownerId: number }[], selfId: number, mates: ReadonlySet<number>,
  ): void {
    const prev = this.markers;
    this.markers = new Set(list.map((m) => m.id));
    if (prev !== null && mateMarkerEdges(prev, list, selfId, mates).length) this.d.local(EV_SQUAD_PING);
  }

  /**
   * The world view: the hulls that changed, the rounds in the water and the
   * contacts that went.
   *
   * A full frame primes and cues nothing. The wire marks an own hull with
   * `own`, so the rules need no player id; `ownFaction` tells a round
   * that comes for this commander from one that goes out.
   */
  onState(s: State, ownFaction: number): void {
    const own = s.fleets.filter((f) => f.own);
    const cues: [string, PlacedCue][] = [];

    // The places go first, because the approach rule reads EVERY own hull
    // and not the hulls of this frame. Every other rule reads a figure the
    // frame is about to replace, so those follow the rules and not this.
    if (s.full) this.ownAt.clear();
    for (const id of s.removed) this.ownAt.delete(id);
    for (const f of own) setPlace(this.ownAt, f);

    if (!s.full) {
      if (reloadEdges(this.reload, own).length) this.d.local(EV_RELOAD_COMPLETE);
      if (fatigueEdges(this.readiness, own).length) this.d.local(EV_FLEET_FATIGUED);
      if (heardEdges(this.heard, own).length) this.d.local(EV_WE_ARE_DETECTED);
      push(cues, 'sfx-scan', scanEdges(this.scan, own));
      // One cue per id per frame, as `push` keeps it: the first hull of
      // each class that anchored this frame (R1143).
      for (const [id, at] of anchorEdges(this.anchor, own)) {
        if (!cues.some(([c]) => c === id)) cues.push([id, at]);
      }
      // A hull this commander did not have and now has left the pens: the
      // first one of the frame, as `push` keeps it.
      for (const f of own) {
        if (this.ownIds.has(f.id)) continue;
        cues.push(['sfx-fleet-departs', placed(f)]);
        break;
      }
      push(cues, 'sfx-torpedo-incoming',
        incomingTorpedoes(this.torpedoIds, s.torpedoes, this.ownAt, ownFaction));
      // The hull edges of R3122, each at the first hull of the frame.
      push(cues, 'sfx-shields-down', shieldsDownEdges(this.shield, own));
      push(cues, 'sfx-magazine-empty', magazineEmptyEdges(this.aboard, own));
      push(cues, 'sfx-round-select',
        roundSelectEdges(this.active, this.roundOrders, this.d.nowSec(), own));
      push(cues, 'sfx-signature-ready', signatureReadyEdges(this.charge, own));
      push(cues, 'sfx-contact-broken', contactBrokenEdges(this.quiet, this.contacted, own));
      push(cues, 'sfx-beacon-hostile',
        hostileBeaconEdges(this.foreignAnchor, s.fleets, ownFaction));
    }

    if (s.full) this.forgetState();
    for (const id of s.removed) this.forgetFleet(id);
    for (const f of s.fleets) {
      if (!f.own) {
        if (f.faction !== ownFaction && f.cls === ShipClass.SHIP_CLASS_BEACON) {
          this.foreignAnchor.set(f.id, f.anchor);
        }
        continue;
      }
      this.anchor.set(f.id, f.anchor);
      // The pulse is the Beacon's own sound, so the class is half the test:
      // an anchored Sentinel is a planted tower and beams nothing (R661).
      if (f.cls === ShipClass.SHIP_CLASS_BEACON
          && f.anchor === AnchorState.ANCHOR_STATE_SET) {
        setPlace(this.beacons, f);
      } else this.beacons.delete(f.id);
      this.ownIds.add(f.id);
      this.reload.set(f.id, f.reloadSec);
      this.readiness.set(f.id, f.readinessPct);
      this.scan.set(f.id, f.scanReadySec);
      this.shield.set(f.id, f.shieldPct);
      this.aboard.set(f.id, roundsAboard(f));
      if (this.active.get(f.id) !== f.activeRound) this.roundOrders.delete(f.id);
      this.active.set(f.id, f.activeRound);
      this.charge.set(f.id, f.signatureCharge);
      this.quiet.set(f.id, f.quiet);
      if (f.heard) this.contacted.add(f.id);
      if (f.quiet) this.contacted.delete(f.id);
      if (f.heard) this.heard.add(f.id); else this.heard.delete(f.id);
      if (f.mission === Mission.MISSION_GATHER) {
        this.gatherCell.set(f.id, cellAt(mpxToPx(f.txMpx), mpxToPx(f.tyMpx)));
      } else this.gatherCell.delete(f.id);
    }
    // The rounds in the water are the whole set of the frame, so an id that
    // is gone is a round that hit, expired or left the view.
    this.torpedoIds.clear();
    for (const t of s.torpedoes) this.torpedoIds.add(t.id);

    if (!this.primedState) { this.primedState = true; return; }
    for (const [id, at] of cues) this.d.cue(id, at.x, at.y);
  }

  /**
   * One site's owner and stock. A deposit that runs dry while one of this
   * commander's Dredgers works its cell is the cue; every other change is
   * not this commander's news.
   */
  onSiteOwner(siteId: number, amount: number): void {
    const was = this.siteAmount.get(siteId);
    this.siteAmount.set(siteId, amount);
    const site = this.d.sites().get(siteId);
    if (!site || site.kind !== SiteKind.SITE_KIND_DEPOSIT) return;
    let worked = false;
    for (const cell of this.gatherCell.values()) {
      if (cell === site.cell) { worked = true; break; }
    }
    if (depletedDeposit(was, amount, worked)) this.d.local(EV_DEPOSIT_DEPLETED);
  }

  /**
   * The frame. Three rules read a clock rather than a wire frame.
   *
   * The day cue sounds on the crossing of dawn and of dusk, and the truce
   * tick counts the closing seconds of the founding truce out (R550). The
   * first frame primes both and sounds neither.
   *
   * One own anchored Beacon sounds every `beacon_pulse_sec`: the one
   * nearest the view centre, which is the one the player is looking at.
   * Another empire's Beacon is silent (R618). The client
   * plays no loop of any kind (audio.md §7), so the pulse is a one-shot the
   * watcher re-triggers. The director's own positional gain damps it, so a
   * Beacon off the screen falls under the audible floor and is never
   * fetched.
   */
  onFrame(viewCentre: Pt, nowSec: number): void {
    const day = isDay(this.d.worldClockSec());
    if (this.wasDay !== null && day !== this.wasDay) this.d.local(EV_DAY_NIGHT);
    this.wasDay = day;

    const left = this.d.truceLeftSec();
    if (truceTick(this.prevTruce, left)) this.d.local(EV_TRUCE_TICK);
    this.prevTruce = left;

    if (nowSec - this.lastPulseSec < AUDIO_BEACON_PULSE_SEC) return;
    const beacon = nearestSetBeacon(this.beacons, viewCentre);
    this.lastPulseSec = nowSec;
    if (beacon) this.d.cue('sfx-beacon-pulse', beacon.x, beacon.y);
  }

  /** An own captain who went from the rise of a career to its peak. */
  private promotions(captains: readonly Captain[]): boolean {
    for (const c of captains) {
      if (c.phase === 'peak' && this.phase.get(c.id) === 'rise') return true;
    }
    return false;
  }

  private forgetState(): void {
    this.ownIds.clear(); this.reload.clear(); this.readiness.clear();
    this.heard.clear(); this.scan.clear(); this.anchor.clear();
    this.beacons.clear(); this.gatherCell.clear();
    this.shield.clear(); this.aboard.clear(); this.active.clear();
    this.charge.clear(); this.quiet.clear(); this.contacted.clear();
    this.roundOrders.clear(); this.foreignAnchor.clear();
  }

  private forgetFleet(id: number): void {
    this.ownIds.delete(id); this.reload.delete(id); this.readiness.delete(id);
    this.heard.delete(id); this.scan.delete(id); this.anchor.delete(id);
    this.beacons.delete(id); this.gatherCell.delete(id);
    this.shield.delete(id); this.aboard.delete(id); this.active.delete(id);
    this.charge.delete(id); this.quiet.delete(id); this.contacted.delete(id);
    this.roundOrders.delete(id); this.foreignAnchor.delete(id);
  }
}

/** The first placed cue of a set, and no more: one cue per id per frame. */
function push(out: [string, PlacedCue][], id: string, at: readonly PlacedCue[]): void {
  if (at.length) out.push([id, at[0]]);
}

/** Replaces a set's whole content. */
function fill(set: Set<number>, ids: Iterable<number>): void {
  set.clear();
  for (const id of ids) set.add(id);
}
